import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    PiPlusBold,
    PiPencilBold,
    PiTrashBold,
    PiCheckBold,
    PiXBold,
    PiInfoBold,
    PiUserBold,
    PiUsersThreeBold,
    PiArrowRightBold,
    PiStarBold,
} from 'react-icons/pi';
import { Button, PageHeader } from '@v2/design-system';
import api from '@v2/lib/api';
import { canViewSellerBonus, hasAnyRole, hasRole, isSuperAdmin } from '@v2/lib/auth';

// ── Types ─────────────────────────────────────────────────────────────────────

type CascadeUser = { id: number; name: string };
type CascadeTeam = { id: number; name: string };

type CascadeRule = {
    id?: number;
    beneficiary_user_id: number;
    beneficiary?: CascadeUser;
    commission_role: string;
    source_type: 'team' | 'user';
    source_team_id: number | null;
    source_team?: CascadeTeam;
    source_user_id: number | null;
    source_user?: CascadeUser;
    rate: number;
    exclude_own_sales: boolean;
    respect_skip_bonus: boolean;
    active: boolean;
    notes: string | null;
};

const EMPTY_RULE: Omit<CascadeRule, 'id'> = {
    beneficiary_user_id: 0,
    commission_role: 'gerente',
    source_type: 'team',
    source_team_id: null,
    source_user_id: null,
    rate: 0,
    exclude_own_sales: true,
    respect_skip_bonus: false,
    active: true,
    notes: null,
};

function rateToPercent(rate: number) {
    return (rate * 100).toFixed(2);
}
function percentToRate(v: string) {
    const parsed = parseFloat(v);
    return isNaN(parsed) ? 0 : parsed / 100;
}

// ── Inline edit row ───────────────────────────────────────────────────────────

type InlineEditRowProps = {
    form: Omit<CascadeRule, 'id'>;
    users: CascadeUser[];
    teams: CascadeTeam[];
    setF: (field: keyof Omit<CascadeRule, 'id'>, value: unknown) => void;
    onSave: () => void;
    onCancel: () => void;
    isPending: boolean;
};

function InlineEditRow({ form, users, teams, setF, onSave, onCancel, isPending }: InlineEditRowProps) {
    const input = 'rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:border-orange-400 focus:outline-none w-full';
    return (
        <tr className="bg-orange-50 border-l-4 border-orange-400">
            <td className="px-3 py-2">
                <select value={form.beneficiary_user_id} onChange={(e) => setF('beneficiary_user_id', Number(e.target.value))} className={input}>
                    <option value={0}>Selecione…</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
            </td>
            <td className="px-3 py-2">
                <input type="text" value={form.commission_role} onChange={(e) => setF('commission_role', e.target.value)}
                    className={input} placeholder="ex: gerente" />
            </td>
            <td className="px-3 py-2 space-y-1.5">
                <select value={form.source_type} onChange={(e) => { setF('source_type', e.target.value); setF('source_team_id', null); setF('source_user_id', null); }} className={input}>
                    <option value="team">Equipe</option>
                    <option value="user">Usuário específico</option>
                </select>
                {form.source_type === 'team' ? (
                    <select value={form.source_team_id ?? ''} onChange={(e) => setF('source_team_id', e.target.value ? Number(e.target.value) : null)} className={input}>
                        <option value="">Selecione equipe…</option>
                        {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                ) : (
                    <select value={form.source_user_id ?? ''} onChange={(e) => setF('source_user_id', e.target.value ? Number(e.target.value) : null)} className={input}>
                        <option value="">Selecione vendedor…</option>
                        {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                )}
            </td>
            <td className="px-3 py-2">
                <div className="flex items-center gap-1">
                    <input type="number" min="0" max="100" step="0.01"
                        value={(form.rate * 100).toFixed(2)}
                        onChange={(e) => setF('rate', parseFloat(e.target.value) / 100 || 0)}
                        className={`${input} w-20`} />
                    <span className="text-xs text-gray-500">%</span>
                </div>
            </td>
            <td className="px-3 py-2 text-center">
                <input type="checkbox" checked={form.exclude_own_sales} onChange={(e) => setF('exclude_own_sales', e.target.checked)} className="rounded" />
            </td>
            <td className="px-3 py-2 text-center">
                <input type="checkbox" checked={form.active} onChange={(e) => setF('active', e.target.checked)} className="rounded" />
            </td>
            <td className="px-3 py-2 text-right">
                <div className="flex items-center justify-end gap-2">
                    <button onClick={onSave} disabled={isPending} className="text-green-600 hover:text-green-700 disabled:opacity-50" title="Salvar">
                        <PiCheckBold className="w-5 h-5" />
                    </button>
                    <button onClick={onCancel} className="text-gray-400 hover:text-red-500" title="Cancelar">
                        <PiXBold className="w-5 h-5" />
                    </button>
                </div>
            </td>
        </tr>
    );
}

// ── Cascade rules section ─────────────────────────────────────────────────────

function CascadeRulesSection() {
    const qc = useQueryClient();
    const [editingId, setEditingId] = useState<number | 'new' | null>(null);
    const [form, setForm] = useState<Omit<CascadeRule, 'id'>>(EMPTY_RULE);

    const { data, isLoading } = useQuery<{
        data: CascadeRule[];
        users: CascadeUser[];
        teams: CascadeTeam[];
    }>({
        queryKey: ['commissioning', 'cascade-rules'],
        queryFn: () => api.get('/commissioning/cascade-rules').then((r) => r.data),
    });

    const rules = data?.data  ?? [];
    const users = data?.users ?? [];
    const teams = data?.teams ?? [];

    const saveMutation = useMutation({
        mutationFn: (payload: Omit<CascadeRule, 'id'> & { id?: number }) =>
            payload.id
                ? api.put(`/commissioning/cascade-rules/${payload.id}`, payload)
                : api.post('/commissioning/cascade-rules', payload),
        onSuccess: () => {
            toast.success('Regra salva!');
            qc.invalidateQueries({ queryKey: ['commissioning', 'cascade-rules'] });
            setEditingId(null);
        },
        onError: () => toast.error('Erro ao salvar regra.'),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: number) => api.delete(`/commissioning/cascade-rules/${id}`),
        onSuccess: () => {
            toast.success('Regra removida.');
            qc.invalidateQueries({ queryKey: ['commissioning', 'cascade-rules'] });
        },
        onError: () => toast.error('Erro ao remover.'),
    });

    function startNew() {
        setForm({ ...EMPTY_RULE });
        setEditingId('new');
    }

    function startEdit(rule: CascadeRule) {
        setForm({
            beneficiary_user_id: rule.beneficiary_user_id,
            commission_role:     rule.commission_role,
            source_type:         rule.source_type,
            source_team_id:      rule.source_team_id,
            source_user_id:      rule.source_user_id,
            rate:                Number(rule.rate),
            exclude_own_sales:   rule.exclude_own_sales,
            respect_skip_bonus:  rule.respect_skip_bonus,
            active:              rule.active,
            notes:               rule.notes,
        });
        setEditingId(rule.id!);
    }

    function setF(field: keyof Omit<CascadeRule, 'id'>, value: unknown) {
        setForm((prev) => ({ ...prev, [field]: value }));
    }

    function save() {
        if (!form.beneficiary_user_id) return void toast.error('Selecione o recebedor.');
        if (form.source_type === 'team' && !form.source_team_id)  return void toast.error('Selecione a equipe.');
        if (form.source_type === 'user' && !form.source_user_id)  return void toast.error('Selecione o vendedor.');
        saveMutation.mutate({
            ...form,
            id: editingId !== 'new' ? (editingId ?? undefined) : undefined,
        });
    }

    const sourceLabel = (rule: CascadeRule) => {
        if (rule.source_type === 'team') return `Equipe: ${rule.source_team?.name ?? `#${rule.source_team_id}`}`;
        return `Usuário: ${rule.source_user?.name ?? `#${rule.source_user_id}`}`;
    };

    return (
        <div className="space-y-4">
            <div className="rounded-xl border border-gray-200 overflow-x-auto bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                        <tr>
                            <th className="px-4 py-3 text-left">Recebedor</th>
                            <th className="px-4 py-3 text-left">Papel</th>
                            <th className="px-4 py-3 text-left">Recebe sobre</th>
                            <th className="px-4 py-3 text-center">%</th>
                            <th className="px-4 py-3 text-center">Excl. próprias</th>
                            <th className="px-4 py-3 text-center">Ativo</th>
                            <th className="px-4 py-3 text-right">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {isLoading && (
                            <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">Carregando…</td></tr>
                        )}
                        {!isLoading && rules.length === 0 && editingId !== 'new' && (
                            <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">Nenhuma regra de liderança configurada.</td></tr>
                        )}
                        {rules.map((rule) =>
                            editingId === rule.id ? (
                                <InlineEditRow key={rule.id} form={form} users={users} teams={teams} setF={setF}
                                    onSave={save} onCancel={() => setEditingId(null)} isPending={saveMutation.isPending} />
                            ) : (
                                <tr key={rule.id} className={`hover:bg-gray-50 transition-colors ${!rule.active ? 'opacity-50' : ''}`}>
                                    <td className="px-4 py-3 font-medium text-gray-900">
                                        <div className="flex items-center gap-1.5">
                                            <PiUserBold className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                            {rule.beneficiary?.name ?? `#${rule.beneficiary_user_id}`}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{rule.commission_role}</span>
                                    </td>
                                    <td className="px-4 py-3 text-gray-600">
                                        <div className="flex items-center gap-1.5">
                                            {rule.source_type === 'team'
                                                ? <PiUsersThreeBold className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                                                : <PiUserBold className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
                                            }
                                            {sourceLabel(rule)}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-center font-semibold text-orange-600">
                                        {(Number(rule.rate) * 100).toFixed(2)}%
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        {rule.exclude_own_sales
                                            ? <PiCheckBold className="w-4 h-4 text-green-500 mx-auto" />
                                            : <PiXBold className="w-4 h-4 text-gray-300 mx-auto" />
                                        }
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        {rule.active
                                            ? <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Sim</span>
                                            : <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Não</span>
                                        }
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button onClick={() => startEdit(rule)} className="text-gray-400 hover:text-orange-600 transition-colors" title="Editar">
                                                <PiPencilBold className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => { if (confirm('Remover esta regra de liderança?')) deleteMutation.mutate(rule.id!); }}
                                                className="text-gray-400 hover:text-red-500 transition-colors"
                                                title="Remover"
                                            >
                                                <PiTrashBold className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )
                        )}
                        {editingId === 'new' && (
                            <InlineEditRow form={form} users={users} teams={teams} setF={setF}
                                onSave={save} onCancel={() => setEditingId(null)} isPending={saveMutation.isPending} />
                        )}
                    </tbody>
                </table>
            </div>

            <Button variant="outline" onClick={startNew} disabled={editingId !== null} className="flex items-center gap-2 text-sm">
                <PiPlusBold className="w-4 h-4" />
                Adicionar Regra
            </Button>
        </div>
    );
}

// ── SDR Links section ─────────────────────────────────────────────────────────

type SdrLink = {
    member_id: number;
    seller: { id: number; name: string } | null;
    team: { id: number; name: string } | null;
    commission_role: string;
    sdr_user_id: number | null;
    sdr: { id: number; name: string } | null;
    sdr_share_rate: number | null;
    is_dual_role: boolean;
};

type SdrLinkUser = { id: number; name: string };

function SdrLinksSection() {
    const qc = useQueryClient();
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form, setForm] = useState<{ sdr_user_id: number | null; sdr_share_rate: number; is_dual_role: boolean }>({
        sdr_user_id: null,
        sdr_share_rate: 0.25,
        is_dual_role: true,
    });

    const { data, isLoading } = useQuery<{ data: SdrLink[]; users: SdrLinkUser[] }>({
        queryKey: ['commissioning', 'sdr-links'],
        queryFn: () => api.get('/commissioning/sdr-links').then((r) => r.data),
    });

    const links  = data?.data  ?? [];
    const users  = data?.users ?? [];

    const updateMutation = useMutation({
        mutationFn: ({ memberId, payload }: { memberId: number; payload: object }) =>
            api.put(`/commissioning/sdr-links/${memberId}`, payload),
        onSuccess: () => {
            toast.success('SDR atualizado!');
            qc.invalidateQueries({ queryKey: ['commissioning', 'sdr-links'] });
            setEditingId(null);
        },
        onError: () => toast.error('Erro ao salvar.'),
    });

    function startEdit(link: SdrLink) {
        setForm({
            sdr_user_id:    link.sdr_user_id,
            sdr_share_rate: link.sdr_share_rate ?? 0.25,
            is_dual_role:   link.is_dual_role,
        });
        setEditingId(link.member_id);
    }

    function save(memberId: number) {
        updateMutation.mutate({ memberId, payload: form });
    }

    const input = 'rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:border-orange-400 focus:outline-none';

    return (
        <div className="rounded-xl border border-gray-200 overflow-x-auto bg-white">
            <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                    <tr>
                        <th className="px-4 py-3 text-left">Vendedor</th>
                        <th className="px-4 py-3 text-left">Equipe</th>
                        <th className="px-4 py-3 text-left">SDR vinculado</th>
                        <th className="px-4 py-3 text-center">% da comissão base</th>
                        <th className="px-4 py-3 text-center">Dupla função</th>
                        <th className="px-4 py-3 text-right">Ações</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {isLoading && (
                        <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">Carregando…</td></tr>
                    )}
                    {links.map((link) =>
                        editingId === link.member_id ? (
                            <tr key={link.member_id} className="bg-orange-50 border-l-4 border-orange-400">
                                <td className="px-4 py-3 font-medium text-gray-900">
                                    {link.seller?.name}
                                </td>
                                <td className="px-4 py-3 text-gray-500 text-xs">
                                    {link.team?.name}
                                </td>
                                <td className="px-3 py-2">
                                    <select
                                        value={form.sdr_user_id ?? ''}
                                        onChange={(e) => setForm((p) => ({ ...p, sdr_user_id: e.target.value ? Number(e.target.value) : null }))}
                                        className={`${input} w-full`}
                                    >
                                        <option value="">Nenhum</option>
                                        {users
                                            .filter((u) => u.id !== link.seller?.id)
                                            .map((u) => <option key={u.id} value={u.id}>{u.name}</option>)
                                        }
                                    </select>
                                </td>
                                <td className="px-3 py-2">
                                    <div className="flex items-center gap-1 justify-center">
                                        <input
                                            type="number" min="0" max="100" step="1"
                                            value={Math.round((form.sdr_share_rate ?? 0.25) * 100)}
                                            onChange={(e) => setForm((p) => ({ ...p, sdr_share_rate: parseFloat(e.target.value) / 100 || 0 }))}
                                            className={`${input} w-20`}
                                            disabled={!form.sdr_user_id}
                                        />
                                        <span className="text-xs text-gray-500">%</span>
                                    </div>
                                </td>
                                <td className="px-3 py-2 text-center">
                                    <input
                                        type="checkbox"
                                        checked={form.is_dual_role}
                                        onChange={(e) => setForm((p) => ({ ...p, is_dual_role: e.target.checked }))}
                                        className="rounded"
                                        disabled={!form.sdr_user_id}
                                    />
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                        <button onClick={() => save(link.member_id)} disabled={updateMutation.isPending}
                                            className="text-green-600 hover:text-green-700 disabled:opacity-50" title="Salvar">
                                            <PiCheckBold className="w-5 h-5" />
                                        </button>
                                        <button onClick={() => setEditingId(null)} className="text-gray-400 hover:text-red-500" title="Cancelar">
                                            <PiXBold className="w-5 h-5" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            <tr key={link.member_id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-3 font-medium text-gray-900">
                                    <div className="flex items-center gap-1.5">
                                        <PiUserBold className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                        {link.seller?.name ?? '—'}
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-gray-500 text-xs">
                                    {link.team?.name ?? '—'}
                                </td>
                                <td className="px-4 py-3">
                                    {link.sdr ? (
                                        <div className="flex items-center gap-1.5 text-purple-700 font-medium">
                                            <PiArrowRightBold className="w-3.5 h-3.5 flex-shrink-0" />
                                            {link.sdr.name}
                                        </div>
                                    ) : (
                                        <span className="text-gray-400 text-xs italic">Nenhum SDR</span>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-center">
                                    {link.sdr_share_rate != null ? (
                                        <span className="font-semibold text-orange-600">
                                            {Math.round(link.sdr_share_rate * 100)}%
                                        </span>
                                    ) : (
                                        <span className="text-gray-300">—</span>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-center">
                                    {link.is_dual_role
                                        ? <PiCheckBold className="w-4 h-4 text-green-500 mx-auto" />
                                        : <PiXBold className="w-4 h-4 text-gray-300 mx-auto" />
                                    }
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <button onClick={() => startEdit(link)} className="text-gray-400 hover:text-orange-600 transition-colors" title="Editar">
                                        <PiPencilBold className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        )
                    )}
                </tbody>
            </table>
        </div>
    );
}

// ── User bonus rates section ──────────────────────────────────────────────────

type BonusUser = {
    user_id: number;
    name: string;
    email?: string | null;
    commission_role: string | null;
    team_name: string | null;
    seller_bonus_rate: number | null;
};

const ROLE_LABEL: Record<string, string> = {
    vendedor: 'Vendedor',
    sdr: 'SDR',
    gerente: 'Gerente',
    diretor: 'Diretor',
    socio_diretor: 'Sócio-Diretor',
};

function UserBonusRatesSection() {
    const qc = useQueryClient();
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editRate, setEditRate] = useState<string>('');
    const [adding, setAdding] = useState(false);
    const [addUserId, setAddUserId] = useState<number | ''>('');
    const [addRate, setAddRate] = useState<string>('');

    const { data, isLoading } = useQuery<{ data: BonusUser[]; users: { id: number; name: string; email?: string }[] }>({
        queryKey: ['commissioning', 'user-bonus-rates'],
        queryFn: () => api.get('/commissioning/user-bonus-rates').then((r) => r.data),
    });

    const updateMutation = useMutation({
        mutationFn: ({ userId, rate }: { userId: number; rate: number | null }) =>
            api.put(`/commissioning/user-bonus-rates/${userId}`, { seller_bonus_rate: rate }),
        onSuccess: () => {
            toast.success('Bônus atualizado!');
            qc.invalidateQueries({ queryKey: ['commissioning', 'user-bonus-rates'] });
            setEditingId(null);
        },
        onError: () => toast.error('Erro ao salvar.'),
    });

    const removeMutation = useMutation({
        mutationFn: (userId: number) =>
            api.put(`/commissioning/user-bonus-rates/${userId}`, { seller_bonus_rate: null }),
        onSuccess: () => {
            toast.success('Bônus removido.');
            qc.invalidateQueries({ queryKey: ['commissioning', 'user-bonus-rates'] });
        },
        onError: () => toast.error('Erro ao remover.'),
    });

    function startEdit(user: BonusUser) {
        setAdding(false);
        setEditingId(user.user_id);
        setEditRate(user.seller_bonus_rate != null ? String(Math.round(user.seller_bonus_rate * 10000) / 100) : '');
    }

    function save(userId: number) {
        const raw = parseFloat(editRate);
        const rate = isNaN(raw) || editRate === '' ? null : raw / 100;
        updateMutation.mutate({ userId, rate });
    }

    function saveAdd() {
        if (!addUserId) return toast.error('Selecione um usuário.');
        const raw = parseFloat(addRate);
        if (isNaN(raw) || raw <= 0) return toast.error('Informe um percentual válido.');
        updateMutation.mutate(
            { userId: Number(addUserId), rate: raw / 100 },
            {
                onSuccess: () => {
                    setAdding(false);
                    setAddUserId('');
                    setAddRate('');
                },
            },
        );
    }

    const members = data?.data ?? [];
    const allUsers = data?.users ?? [];

    // Only show team members + users with bonus set (extras are already included from backend)
    // Filter out users already in the list for the add selector
    const existingIds = new Set(members.map((u) => u.user_id));
    const addableUsers = allUsers.filter((u) => !existingIds.has(u.id));

    return (
        <div className="space-y-3">
            <div className="rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Vendedor</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Time</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Papel</th>
                            <th className="px-4 py-3 text-center font-semibold text-gray-700">Bônus adicional</th>
                            <th className="px-4 py-3" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {isLoading ? (
                            <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">Carregando…</td></tr>
                        ) : members.length === 0 ? (
                            <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">Nenhum membro encontrado.</td></tr>
                        ) : members.map((u) =>
                            editingId === u.user_id ? (
                                <tr key={u.user_id} className="bg-yellow-50">
                                    <td className="px-4 py-2 font-medium text-gray-900">{u.name}</td>
                                    <td className="px-4 py-2 text-gray-500 text-xs">{u.team_name ?? '—'}</td>
                                    <td className="px-4 py-2 text-gray-500 text-xs">{u.commission_role ? (ROLE_LABEL[u.commission_role] ?? u.commission_role) : '—'}</td>
                                    <td className="px-3 py-2 text-center">
                                        <div className="flex items-center justify-center gap-1">
                                            <input
                                                type="number" min="0" max="100" step="0.01"
                                                value={editRate}
                                                onChange={(e) => setEditRate(e.target.value)}
                                                placeholder="0"
                                                autoFocus
                                                className="w-20 rounded-lg border border-gray-300 px-2 py-1 text-sm text-center focus:border-orange-400 focus:outline-none"
                                            />
                                            <span className="text-sm text-gray-500">%</span>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button onClick={() => save(u.user_id)} disabled={updateMutation.isPending}
                                                className="text-green-600 hover:text-green-700 disabled:opacity-50" title="Salvar">
                                                <PiCheckBold className="w-5 h-5" />
                                            </button>
                                            <button onClick={() => setEditingId(null)} className="text-gray-400 hover:text-red-500" title="Cancelar">
                                                <PiXBold className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                <tr key={u.user_id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-4 py-3 font-medium text-gray-900">
                                        <div className="flex items-center gap-1.5">
                                            <PiUserBold className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                            <span>
                                                {u.name}
                                                {u.email && (
                                                    <span className="mt-0.5 block text-[11px] font-normal text-gray-400">{u.email}</span>
                                                )}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-gray-500 text-xs">{u.team_name ?? '—'}</td>
                                    <td className="px-4 py-3 text-gray-500 text-xs">{u.commission_role ? (ROLE_LABEL[u.commission_role] ?? u.commission_role) : '—'}</td>
                                    <td className="px-4 py-3 text-center">
                                        {u.seller_bonus_rate != null && u.seller_bonus_rate > 0 ? (
                                            <span className="inline-flex items-center gap-1 font-semibold text-amber-600">
                                                <PiStarBold className="w-3.5 h-3.5" />
                                                +{Math.round(u.seller_bonus_rate * 10000) / 100}%
                                            </span>
                                        ) : (
                                            <span className="text-gray-300 text-xs">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button onClick={() => startEdit(u)} className="text-gray-400 hover:text-orange-600 transition-colors" title="Editar bônus">
                                                <PiPencilBold className="w-4 h-4" />
                                            </button>
                                            {u.seller_bonus_rate != null && u.seller_bonus_rate > 0 && (
                                                <button
                                                    onClick={() => removeMutation.mutate(u.user_id)}
                                                    disabled={removeMutation.isPending}
                                                    className="text-gray-400 hover:text-red-500 disabled:opacity-40 transition-colors"
                                                    title="Remover bônus"
                                                >
                                                    <PiTrashBold className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )
                        )}

                        {/* Linha de adição */}
                        {adding && (
                            <tr className="bg-green-50 border-t border-green-200">
                                <td colSpan={3} className="px-4 py-2">
                                    <select
                                        value={addUserId}
                                        onChange={(e) => setAddUserId(e.target.value ? Number(e.target.value) : '')}
                                        autoFocus
                                        className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-orange-400 focus:outline-none"
                                    >
                                        <option value="">Selecione o usuário…</option>
                                        {addableUsers.map((u) => (
                                            <option key={u.id} value={u.id}>
                                                {u.name}{u.email ? ` (${u.email})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </td>
                                <td className="px-3 py-2 text-center">
                                    <div className="flex items-center justify-center gap-1">
                                        <input
                                            type="number" min="0.01" max="100" step="0.01"
                                            value={addRate}
                                            onChange={(e) => setAddRate(e.target.value)}
                                            placeholder="ex: 1.5"
                                            className="w-20 rounded-lg border border-gray-300 px-2 py-1 text-sm text-center focus:border-orange-400 focus:outline-none"
                                        />
                                        <span className="text-sm text-gray-500">%</span>
                                    </div>
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                        <button onClick={saveAdd} disabled={updateMutation.isPending}
                                            className="text-green-600 hover:text-green-700 disabled:opacity-50" title="Confirmar">
                                            <PiCheckBold className="w-5 h-5" />
                                        </button>
                                        <button onClick={() => { setAdding(false); setAddUserId(''); setAddRate(''); }}
                                            className="text-gray-400 hover:text-red-500" title="Cancelar">
                                            <PiXBold className="w-5 h-5" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {!adding && (
                <div className="flex justify-end">
                    <Button variant="outline" onClick={() => { setAdding(true); setEditingId(null); }} className="flex items-center gap-2 text-sm">
                        <PiPlusBold className="w-4 h-4" />
                        Adicionar bônus
                    </Button>
                </div>
            )}
        </div>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function CommissionStructuresPage() {
    return (
        <div className="space-y-10">
            <PageHeader
                title="Estruturas de Comissão"
                subtitle="Configure regras de liderança (cascata) e links SDR por vendedor"
            />

            {/* ── Seção 1: Comissão de Liderança ── */}
            <section className="space-y-4">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <PiUsersThreeBold className="w-5 h-5 text-blue-500" />
                        Comissão de Liderança (Cascata)
                    </h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                        Configurável por usuário. Define <strong>quem recebe</strong>, <strong>sobre quem recebe</strong> e o <strong>percentual</strong>.
                        Calculada por parcela efetivamente paga pelo cliente.
                    </p>
                </div>

                <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 flex gap-2 text-sm text-blue-800">
                    <PiInfoBold className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                        <strong>Equipe</strong> = aplica a todos os vendedores da equipe.{' '}
                        <strong>Usuário específico</strong> = aplica apenas quando aquele vendedor fecha um deal.{' '}
                        <strong>Excl. próprias</strong> = não gera comissão quando o recebedor é o próprio vendedor.
                    </span>
                </div>

                <CascadeRulesSection />
            </section>

            <hr className="border-gray-200" />

            {/* ── Seção 2: SDR por Vendedor ── */}
            <section className="space-y-4">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <PiArrowRightBold className="w-5 h-5 text-purple-500" />
                        SDR por Vendedor
                    </h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                        Quando o SDR passa um lead para o vendedor e a venda converte, o SDR recebe um percentual da comissão base do vendedor.
                        {canViewSellerBonus() && (
                            <> O bônus adicional de 1,5% (Sócio-Diretor) <strong>não entra</strong> no cálculo do SDR.</>
                        )}
                    </p>
                </div>

                <div className="rounded-xl border border-purple-200 bg-purple-50 px-4 py-3 flex gap-2 text-sm text-purple-800">
                    <PiInfoBold className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                        Selecione o <strong>SDR</strong> responsável por cada vendedor e o <strong>percentual</strong> sobre a comissão base.
                        O SDR só recebe quando a parcela do cliente é efetivamente paga.
                    </span>
                </div>

                <SdrLinksSection />
            </section>

            {(isSuperAdmin() || hasRole('admin') || hasAnyRole(['financeiro', 'financial', 'financial_director', 'hr'])) && (
                <>
                    <hr className="border-gray-200" />

                    {/* ── Seção 3: Bônus Pessoal por Vendedor ── */}
                    <section className="space-y-4">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <PiStarBold className="w-5 h-5 text-amber-500" />
                                Bônus Pessoal por Vendedor
                            </h2>
                            <p className="text-sm text-gray-500 mt-0.5">
                                Adicional fixo sobre o valor do deal, calculado para o próprio vendedor.
                                Sobrepõe o bônus configurado na estrutura por nível.
                            </p>
                        </div>

                        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex gap-2 text-sm text-amber-800">
                            <PiInfoBold className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <span>
                                Atualmente, apenas <strong>Rafael Zanussi</strong> possui bônus adicional configurado (+1,5%).
                                Deixe o campo vazio para remover o bônus de um vendedor.
                            </span>
                        </div>

                        <UserBonusRatesSection />
                    </section>
                </>
            )}
        </div>
    );
}
